import React, { useEffect, useRef, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FiChevronDown } from 'react-icons/fi';
import { useApp } from '../../context/AppContext';
import Message from './Message';
import Emoji from '../Common/Emoji';

function stripHtml(html) {
  if (typeof html !== 'string') return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
}

function isMessageInDateRange(message, dateFilter) {
  if (dateFilter === 'all') return true;
  const raw = message.created_at || message.createdAt;
  if (!raw) return false;
  const msgDate = new Date(raw);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000 - 1);
  const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
  const yesterdayEnd = new Date(todayStart.getTime() - 1);
  if (dateFilter === 'today') return msgDate >= todayStart && msgDate <= todayEnd;
  if (dateFilter === 'yesterday') return msgDate >= yesterdayStart && msgDate <= yesterdayEnd;
  if (dateFilter === 'last7') return msgDate >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  if (dateFilter === 'last30') return msgDate >= new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  return true;
}

const MessagesContainer = styled.div`
  flex: 1;
  padding: 1.5rem 2rem 1.25rem 2rem;
  overflow-y: auto;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  margin: 0;
  -webkit-user-select: text;
  user-select: text;
  position: relative;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.06);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #2193b0;
    border-radius: 4px;
    &:hover {
      background: #6dd5ed;
    }
  }
`;

const ListWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  min-height: 0;
`;

const ScrollToBottomBtn = styled.button`
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(33, 147, 176, 0.5);
  transition: transform 0.2s, box-shadow 0.2s;
  z-index: 10;
  pointer-events: auto;
  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 24px rgba(33, 147, 176, 0.6);
  }
  &:active {
    transform: scale(0.98);
  }
`;

const BOTTOM_TOLERANCE = 50;

export default function MessageList({ searchQuery = '', dateFilter = 'all' }) {
  const { state } = useApp();
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const flatMessages = useMemo(() => {
    const list = state.messages || [];
    const hasSearch = searchQuery.trim() || dateFilter !== 'all';
    if (!hasSearch) return list;
    const q = searchQuery.trim().toLowerCase();
    return list.filter((msg) => {
      if (!isMessageInDateRange(msg, dateFilter)) return false;
      if (!q) return true;
      const text = stripHtml(msg.content || '').toLowerCase();
      return text.includes(q);
    });
  }, [state.messages, searchQuery, dateFilter]);


  // Скроллим вниз всегда при смене чата, а при новых сообщениях — только если пользователь был внизу
  const scrollToBottom = (smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    }
  };

  // Проверка: пользователь внизу или нет (для кнопки «Вниз»)
  const wasAtBottom = useRef(true);
  const hasActiveSearch = Boolean(searchQuery.trim() || dateFilter !== 'all');

  const checkAtBottom = () => {
    const container = containerRef.current;
    if (!container) return;
    const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < BOTTOM_TOLERANCE;
    wasAtBottom.current = atBottom;
    setShowScrollToBottom(!atBottom);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < BOTTOM_TOLERANCE;
      wasAtBottom.current = atBottom;
      setShowScrollToBottom(!atBottom);
    };
    container.addEventListener('scroll', handleScroll);
    const t = setTimeout(checkAtBottom, 150);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(t);
    };
  }, []);

  // Обновлять видимость кнопки при смене списка сообщений (фильтр, новые сообщения)
  useEffect(() => {
    const t = setTimeout(checkAtBottom, 100);
    return () => clearTimeout(t);
  }, [flatMessages.length, hasActiveSearch]);

  const prevMessagesLengthRef = useRef(0);
  useEffect(() => {
    const prevLen = prevMessagesLengthRef.current;
    const nextLen = state.messages.length;
    prevMessagesLengthRef.current = nextLen;
    // Вариант Б: всегда автоскролл вниз при появлении новых сообщений,
    // как в классических мессенджерах (новые сообщения внизу).
    if (nextLen > prevLen) {
      scrollToBottom();
      setTimeout(() => scrollToBottom(false), 100);
    }
  }, [state.messages]);

  // При смене чата всегда скроллим вниз
  useEffect(() => {
    scrollToBottom(false);
  }, [state.currentChat]);

  useEffect(() => {
    if (state.currentChat) {
      window.socket?.emit('mark_chat_read', state.currentChat.id);
    }
  }, [state.currentChat]);

  const canShowScrollBtn = showScrollToBottom || (hasActiveSearch && flatMessages.length > 0);

  return (
    <ListWrapper>
      <MessagesContainer ref={containerRef}>
        {hasActiveSearch && flatMessages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.7)', fontSize: '1rem' }}>
            Нет сообщений по заданным условиям поиска
          </div>
        )}
        {flatMessages.map((message) => (
          <div key={message.id} id={`message-${message.id}`}>
            <Message 
              message={message} 
              showAvatar={true}
              highlightQuery={searchQuery.trim() || undefined}
            />
          </div>
        ))}
        <div ref={messagesEndRef} />
      </MessagesContainer>
      {canShowScrollBtn && (
        <ScrollToBottomBtn
          type="button"
          onClick={() => scrollToBottom(true)}
          title="К новым сообщениям"
          aria-label="К новым сообщениям"
        >
          <FiChevronDown size={24} />
        </ScrollToBottomBtn>
      )}
    </ListWrapper>
  );
}